import type { PurchaseOrderInvoice } from '@/types/purchaseOrder.types';
import type { ColorDeBadge } from '@/shared/components/Badge';

/**
 * Estado del CFDI de complemento de una factura (C-09).
 *
 * Se calcula aqui y no en el back porque `GET :id/invoices` ya devuelve los
 * pagos con su `complement_status`: agregar un campo derivado al contrato seria
 * una segunda verdad que puede desincronizarse de esta.
 *
 * Lo que NO se calcula aqui es el plazo de los 10 dias habiles. Esa cuenta vive
 * solo en el backend, con los festivos del articulo 74; duplicarla en el front
 * abriria la puerta a que las dos mitades no coincidan y el proveedor viera un
 * "te quedan 2 dias" mientras el servidor ya lo tiene bloqueado.
 */
export type EstadoDeComplemento =
  | 'sin_pagos'
  | 'pago_parcial'
  | 'sin_complemento'
  | 'complemento_rechazado'
  | 'completa';

/** La misma tolerancia con la que el backend decide que una factura esta saldada. */
const TOLERANCIA = 0.02;

export function estadoDeComplemento(
  invoice: PurchaseOrderInvoice,
): EstadoDeComplemento {
  const pagos = invoice.payments ?? [];
  if (pagos.length === 0) return 'sin_pagos';

  const total = Number(invoice.invoice_total ?? 0);
  const pagado = pagos.reduce((suma, p) => suma + Number(p.amount || 0), 0);
  if (total <= 0 || pagado < total - TOLERANCIA) return 'pago_parcial';

  // Todos los pagos necesitan su complemento valido: uno solo sin el deja la
  // factura incumplida, que es como lo cuenta el checker del backend.
  if (pagos.every((p) => p.complement_status === 'validated')) return 'completa';
  if (pagos.some((p) => p.complement_status === 'rejected')) {
    return 'complemento_rechazado';
  }
  return 'sin_complemento';
}

interface EtiquetaDeFactura {
  texto: string;
  color: ColorDeBadge;
}

/**
 * La etiqueta a mostrar, o null cuando no hay nada que señalar.
 *
 * Solo se marcan los estados que le piden algo al proveedor. Una factura al
 * corriente no lleva badge: en una lista, marcar lo normal es ruido y entierra
 * lo que si importa.
 */
export function etiquetaDeFactura(
  invoice: PurchaseOrderInvoice,
): EtiquetaDeFactura | null {
  switch (estadoDeComplemento(invoice)) {
    case 'sin_complemento':
      return {
        texto: 'Pagada sin complemento',
        color: 'naranja',
      };
    case 'complemento_rechazado':
      return {
        texto: 'Complemento rechazado',
        color: 'rojo',
      };
    default:
      return null;
  }
}
